// Schema-v2 book manuscript model for the design studio.
//
// Mirrors services/shared/infrastructure/manuscript_schema.py so the client and
// server agree on the page/block model and the v1->v2 upgrade. The design doc is
// docs/design/DESIGN_STUDIO_PAGE_BLOCK_MODEL.md.
//
// A manuscript is an ordered list of layout units. Each unit is either a
// reflowable `flow_section` (text books) or a fixed `page`/`spread` canvas
// (picture books); a mixed book interleaves them. The flow editor (P2) authors
// flow_section units and passes fixed units through untouched.

export const SCHEMA_VERSION = 2;

export type BookType = 'text' | 'picture' | 'mixed';

export interface Crop {
  top_percent: number;
  right_percent: number;
  bottom_percent: number;
  left_percent: number;
}

export interface Frame {
  x_percent: number;
  y_percent: number;
  width_percent: number;
  height_percent: number;
  rotation_degrees: number;
}

export type FlowImageLayout = 'inline' | 'float' | 'full_width' | 'full_page' | 'full_bleed';

export interface HeadingBlock {
  type: 'heading';
  level: 'sub_chapter' | 'sub_sub_chapter';
  text: string;
}

export interface ParagraphBlock {
  type: 'paragraph';
  id: string;
  text: string;
  style: 'body' | 'quote';
  indent_level: number;
}

export interface ImageFlowBlock {
  type: 'image';
  id: string;
  artifact_id: number;
  layout: FlowImageLayout;
  align: 'left' | 'center' | 'right';
  width_percent: number;
  break_before: string;
  caption: string;
  filter: string;
  crop: Crop;
}

export interface SpacerBlock {
  type: 'spacer';
  height_cm: number;
}

export interface PageBreakBlock {
  type: 'page_break';
  to_side: string;
}

export type FlowBlock =
  | HeadingBlock
  | ParagraphBlock
  | ImageFlowBlock
  | SpacerBlock
  | PageBreakBlock;

export interface FixedBlock {
  id: string;
  type: 'image' | 'text';
  artifact_id?: number;
  text?: string;
  text_style?: Record<string, unknown>;
  frame: Frame;
  z_index: number;
  filter: string;
  crop: Crop;
  caption?: string;
  on_page: 'left' | 'right' | 'both';
}

export interface Background {
  type: 'image' | 'color';
  artifact_id?: number;
  fit?: string;
  focal_x_percent?: number;
  focal_y_percent?: number;
  filter?: string;
  color?: string;
}

export interface FlowSectionUnit {
  kind: 'flow_section';
  id: string;
  title: string;
  title_level: 'chapter' | 'sub_chapter' | 'sub_sub_chapter' | 'none';
  start_side: 'left' | 'right' | 'next';
  blocks: FlowBlock[];
}

export interface FixedPageUnit {
  kind: 'page' | 'spread';
  id: string;
  page_role: string;
  start_side: 'left' | 'right' | 'next';
  background: Background | null;
  blocks: FixedBlock[];
}

export type LayoutUnit = FlowSectionUnit | FixedPageUnit;

export interface ManuscriptV2 {
  schema_version: 2;
  book_type: BookType;
  front_matter: LayoutUnit[];
  content: LayoutUnit[];
  back_matter: LayoutUnit[];
}

// --- defensive JSON accessors ---------------------------------------------

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asText = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asNumber = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const emptyCrop = (): Crop => ({
  top_percent: 0,
  right_percent: 0,
  bottom_percent: 0,
  left_percent: 0,
});

const cropFrom = (value: unknown): Crop => {
  const record = asRecord(value);
  return {
    top_percent: asNumber(record.top_percent ?? record.crop_top_percent, 0),
    right_percent: asNumber(record.right_percent ?? record.crop_right_percent, 0),
    bottom_percent: asNumber(record.bottom_percent ?? record.crop_bottom_percent, 0),
    left_percent: asNumber(record.left_percent ?? record.crop_left_percent, 0),
  };
};

// --- version detection -----------------------------------------------------

export const detectSchemaVersion = (manuscript: unknown): number => {
  const record = asRecord(manuscript);
  const version = record.schema_version;
  if (typeof version === 'number' && version >= 2) {
    return version;
  }
  if ('content' in record && !('chapters' in record)) {
    return 2;
  }
  return 1;
};

// --- v1 -> v2 upgrade (mirror of the backend) ------------------------------

const FLOW_IMAGE_LAYOUT: Record<string, FlowImageLayout> = {
  after_paragraph: 'inline',
  top_of_next_page: 'full_width',
  full_page: 'full_page',
};

const pageBreakSide = (pageSide: string): string =>
  pageSide === 'left' || pageSide === 'right' ? pageSide : 'page';

const paragraphBlockFromV1 = (paragraph: Record<string, unknown>): ParagraphBlock => {
  const level = asText(paragraph.level, 'paragraph');
  const indent = { paragraph: 0, sub_paragraph: 1, sub_sub_paragraph: 2 }[level] ?? 0;
  return {
    type: 'paragraph',
    id: asText(paragraph.id) || 'p',
    text: asText(paragraph.text),
    style: level === 'quote' ? 'quote' : 'body',
    indent_level: indent,
  };
};

const imageBlockFromV1 = (placement: Record<string, unknown>, layout: FlowImageLayout): ImageFlowBlock => {
  const block: ImageFlowBlock = {
    type: 'image',
    id: asText(placement.id) || 'img',
    artifact_id: Math.trunc(asNumber(placement.artifact_id, 0)),
    layout,
    align: (asText(placement.alignment, 'center') as ImageFlowBlock['align']) || 'center',
    width_percent: asNumber(placement.width_percent, 60),
    break_before: 'auto',
    caption: asText(placement.caption),
    filter: asText(placement.filter, 'none'),
    crop: cropFrom(placement),
  };
  if (layout === 'full_width' || layout === 'full_page' || layout === 'full_bleed') {
    block.break_before = pageBreakSide(asText(placement.page_side, 'next'));
  }
  return block;
};

const absolutePlacementToPage = (placement: Record<string, unknown>): FixedPageUnit => ({
  kind: 'page',
  id: `page-${asText(placement.id) || 'image'}`,
  page_role: 'full_bleed_image',
  start_side: 'next',
  background: null,
  blocks: [
    {
      id: asText(placement.id) || 'fb',
      type: 'image',
      artifact_id: Math.trunc(asNumber(placement.artifact_id, 0)),
      frame: {
        x_percent: asNumber(placement.x_percent, 50),
        y_percent: asNumber(placement.y_percent, 50),
        width_percent: asNumber(placement.width_percent, 60),
        height_percent: asNumber(placement.height_percent, 35),
        rotation_degrees: asNumber(placement.rotation_degrees, 0),
      },
      z_index: 1,
      filter: asText(placement.filter, 'none'),
      crop: cropFrom(placement),
      caption: asText(placement.caption),
      on_page: 'left',
    },
  ],
});

const chapterToUnits = (chapter: Record<string, unknown>): LayoutUnit[] => {
  const placements = asArray(chapter.image_placements).map(asRecord);
  const paragraphIds = new Set(asArray(chapter.paragraphs).map((p) => asText(asRecord(p).id)));
  const byAnchor = new Map<string, ImageFlowBlock[]>();
  const absoluteUnits: FixedPageUnit[] = [];
  const unanchored: ImageFlowBlock[] = [];

  placements.forEach((placement) => {
    const mode = asText(placement.placement_mode ?? placement.placement, 'after_paragraph');
    if (mode === 'absolute') {
      absoluteUnits.push(absolutePlacementToPage(placement));
      return;
    }
    const layout = FLOW_IMAGE_LAYOUT[mode] ?? 'inline';
    const anchor = asText(placement.anchor_paragraph_id);
    if (anchor && paragraphIds.has(anchor)) {
      const list = byAnchor.get(anchor) ?? [];
      list.push(imageBlockFromV1(placement, layout));
      byAnchor.set(anchor, list);
    } else {
      unanchored.push(imageBlockFromV1(placement, layout));
    }
  });

  const blocks: FlowBlock[] = [];
  asArray(chapter.paragraphs).forEach((paragraph) => {
    const record = asRecord(paragraph);
    blocks.push(paragraphBlockFromV1(record));
    const anchored = byAnchor.get(asText(record.id));
    if (anchored) {
      blocks.push(...anchored);
    }
  });
  blocks.push(...unanchored);

  const section: FlowSectionUnit = {
    kind: 'flow_section',
    id: asText(chapter.id) || 'sec',
    title: asText(chapter.title),
    title_level: 'chapter',
    start_side: 'next',
    blocks,
  };
  return [section, ...absoluteUnits];
};

const matterToUnits = (matter: unknown): LayoutUnit[] => {
  const units: LayoutUnit[] = [];
  asArray(matter).forEach((item) => {
    const record = asRecord(item);
    if (Object.keys(record).length === 0) {
      return;
    }
    if ('kind' in record) {
      units.push(record as unknown as LayoutUnit);
    } else if ('paragraphs' in record || 'title' in record || 'image_placements' in record) {
      units.push(...chapterToUnits(record));
    }
  });
  return units;
};

export const upgradeManuscriptV1ToV2 = (manuscript: unknown, bookType: BookType = 'text'): ManuscriptV2 => {
  const record = asRecord(manuscript);
  const content: LayoutUnit[] = [];
  asArray(record.chapters).forEach((chapter) => {
    content.push(...chapterToUnits(asRecord(chapter)));
  });
  return {
    schema_version: SCHEMA_VERSION,
    book_type: bookType,
    front_matter: matterToUnits(record.front_matter),
    content,
    back_matter: matterToUnits(record.back_matter),
  };
};

export const ensureV2 = (manuscript: unknown, bookType: BookType = 'text'): ManuscriptV2 => {
  if (detectSchemaVersion(manuscript) >= 2) {
    const record = asRecord(manuscript);
    return {
      schema_version: SCHEMA_VERSION,
      book_type: (asText(record.book_type, bookType) as BookType) || bookType,
      front_matter: asArray(record.front_matter) as LayoutUnit[],
      content: asArray(record.content) as LayoutUnit[],
      back_matter: asArray(record.back_matter) as LayoutUnit[],
    };
  }
  return upgradeManuscriptV1ToV2(manuscript, bookType);
};

// --- editor construction helpers ------------------------------------------

let idCounter = 0;
const nextId = (prefix: string): string => {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
};

export const createParagraphBlock = (text = ''): ParagraphBlock => ({
  type: 'paragraph',
  id: nextId('p'),
  text,
  style: 'body',
  indent_level: 0,
});

export const createHeadingBlock = (level: HeadingBlock['level'] = 'sub_chapter'): HeadingBlock => ({
  type: 'heading',
  level,
  text: '',
});

export const createImageBlock = (artifactId: number): ImageFlowBlock => ({
  type: 'image',
  id: nextId('img'),
  artifact_id: artifactId,
  layout: 'inline',
  align: 'center',
  width_percent: 60,
  break_before: 'auto',
  caption: '',
  filter: 'none',
  crop: emptyCrop(),
});

export const createFlowSection = (title = 'Untitled Section'): FlowSectionUnit => ({
  kind: 'flow_section',
  id: nextId('sec'),
  title,
  title_level: 'chapter',
  start_side: 'next',
  blocks: [createParagraphBlock()],
});

// Split plain text (e.g. a story artifact's text_content) into paragraph blocks.
export const paragraphBlocksFromText = (text: string): ParagraphBlock[] =>
  text
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => createParagraphBlock(chunk));

export const createManuscriptV2 = (bookType: BookType, sections: FlowSectionUnit[] = []): ManuscriptV2 => ({
  schema_version: SCHEMA_VERSION,
  book_type: bookType,
  front_matter: [],
  content: sections.length > 0 ? sections : [createFlowSection()],
  back_matter: [],
});

// --- fixed page/spread construction (canvas editor, P3) --------------------

const centeredFrame = (): Frame => ({
  x_percent: 50,
  y_percent: 50,
  width_percent: 60,
  height_percent: 40,
  rotation_degrees: 0,
});

export const createFixedImageBlock = (artifactId: number, onPage: FixedBlock['on_page'] = 'left'): FixedBlock => ({
  id: nextId('fb'),
  type: 'image',
  artifact_id: artifactId,
  frame: centeredFrame(),
  z_index: 1,
  filter: 'none',
  crop: emptyCrop(),
  caption: '',
  on_page: onPage,
});

export const createFixedTextBlock = (onPage: FixedBlock['on_page'] = 'left'): FixedBlock => ({
  id: nextId('fb'),
  type: 'text',
  text: '',
  text_style: { role: 'caption', align: 'center', color: '#111111', size_pt: 18, bold: false, italic: false },
  frame: { ...centeredFrame(), height_percent: 12 },
  z_index: 2,
  filter: 'none',
  crop: emptyCrop(),
  on_page: onPage,
});

export const createFixedPageUnit = (kind: 'page' | 'spread'): FixedPageUnit => ({
  kind,
  id: nextId(kind),
  page_role: 'content',
  start_side: 'next',
  background: null,
  blocks: [],
});

export const isFlowSection = (unit: LayoutUnit): unit is FlowSectionUnit => unit.kind === 'flow_section';

export const isFixedPage = (unit: LayoutUnit): unit is FixedPageUnit =>
  unit.kind === 'page' || unit.kind === 'spread';
